import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'Button';
import { Text } from 'Text';
import { showToast } from 'Toast';
import { useEventLogger } from 'FrontRoyalAngular';
import { angularInjectorProvider } from 'Injector';
import { animatePathChange } from 'RouteAnimation';
import { getCohort, type CurrentUser } from 'Users';
import type TimerSingleton from 'FrontRoyalTimer/TimerSingleton';
import { type BrowseFilters, type BrowseStream, type DisplayMode, type ProgressStatus } from '../BrowseCourses.types';
import { buildFilterIndex, filterStreams } from '../filterStreams';
import { useBrowseCoursesFilters } from '../hooks/useBrowseCoursesFilters';
import { useDisplayMode } from '../hooks/useDisplayMode';
import { useStreamAccess } from '../hooks/useStreamAccess';
import { searchFiltersUpdated } from '../searchAttempt';
import { getStreamPath } from '../selectors';
import { CourseFilterGroups } from './CourseFilterGroups';
import { CourseGrid } from './CourseGrid';
import { CourseSearchInput } from './CourseSearchInput';
import { CourseUnavailableModal } from './CourseUnavailableModal';
import { DisplayModeToggle } from './DisplayModeToggle';
import { MobileFilters } from './MobileFilters';
import '../locales/browseCourses/browseCourses-en.json';

type Props = {
    streams: BrowseStream[];
    currentUser: CurrentUser | null;
};

function useCourseActions(t: (key: string) => string) {
    const EventLogger = useEventLogger();

    const launch = useCallback((stream: BrowseStream) => {
        animatePathChange(getStreamPath(stream));
        // The stream dashboard finishes this timer and logs rendered_stream_dashboard
        angularInjectorProvider
            .get<TimerSingleton>('timerSingleton')
            .startTimer(`clickedToNavigateToStreamDashboard-${stream.id}`);
    }, []);

    const notify = useCallback(
        (stream: BrowseStream) => {
            EventLogger.log('lesson:stream:notify-coming-soon', {
                lesson_stream_id: stream.id,
                lesson_stream_complete: !!stream.lesson_streams_progress?.complete,
                lesson_stream_version_id: stream.version_id,
                exam: stream.exam,
            });
            showToast({ message: t('letYouKnow'), type: 'success' });
        },
        [EventLogger, t],
    );

    return { launch, notify };
}

// A new currentUser object rebuilds the results with the same content. Log only real changes.
function useSearchAttemptUpdates(filters: BrowseFilters, filteredStreams: BrowseStream[], displayMode: DisplayMode) {
    const lastSearchUpdate = useRef<string>(undefined);
    useEffect(() => {
        const update = {
            topics: filters.topics,
            progressIds: filters.progress,
            searchText: filters.searchText,
            resultStreamIds: filteredStreams.map(stream => stream.id),
        };
        const key = JSON.stringify({ ...update, displayMode });
        if (key === lastSearchUpdate.current) return;
        lastSearchUpdate.current = key;
        searchFiltersUpdated(update);
    }, [filters, filteredStreams, displayMode]);
}

export const BrowseCoursesContent: FC<Props> = ({ streams, currentUser }) => {
    const { t, i18n } = useTranslation('back_royal', { keyPrefix: 'browseCourses.browseCourses' });
    const [displayMode, setDisplayMode] = useDisplayMode(currentUser);
    const [unavailableStream, setUnavailableStream] = useState<BrowseStream | null>(null);
    const accessByStreamId = useStreamAccess(streams, currentUser);
    const { launch, notify } = useCourseActions(t);

    const index = useMemo(
        () => buildFilterIndex(streams, { localeCode: i18n.language, cohort: getCohort(currentUser), now: Date.now() }),
        [streams, i18n.language, currentUser],
    );

    const progressNames = useMemo<Record<ProgressStatus, string>>(
        () => ({ not_started: t('not_started'), in_progress: t('in_progress'), completed: t('completed') }),
        [t],
    );

    const filterState = useBrowseCoursesFilters({
        availableTopics: index.availableTopics,
        visibleStatusOptions: index.visibleStatusOptions,
        progressNames,
    });
    const { filters, draft } = filterState;

    const filteredStreams = useMemo(
        () => filterStreams(streams, index, filters, id => !!accessByStreamId.get(id)?.canLaunch),
        [streams, index, filters, accessByStreamId],
    );

    useSearchAttemptUpdates(filters, filteredStreams, displayMode);

    const onPressStream = useCallback(
        (stream: BrowseStream) => {
            if (accessByStreamId.get(stream.id)?.canLaunch) launch(stream);
            else setUnavailableStream(stream);
        },
        [accessByStreamId, launch],
    );

    const filterGroups = (
        <CourseFilterGroups
            draft={draft}
            availableTopics={index.availableTopics}
            visibleStatusOptions={index.visibleStatusOptions}
            showProgress={!!currentUser}
            clearTopics={filterState.clearTopics}
            toggleTopic={filterState.toggleTopic}
            clearProgress={filterState.clearProgress}
            toggleProgress={filterState.toggleProgress}
            toggleStatus={filterState.toggleStatus}
        />
    );

    const unavailableAccess = unavailableStream && accessByStreamId.get(unavailableStream.id);

    return (
        <div className="flex flex-col gap-5">
            <CourseSearchInput value={filters.searchText} onChange={filterState.setSearchText} />

            <div className="mb-2.5 max-sm:hidden">{filterGroups}</div>
            <MobileFilters
                filters={filters}
                isOpen={filterState.drawerOpen}
                onOpen={filterState.openDrawer}
                onSave={filterState.saveDrawer}
                onCancel={filterState.cancelDrawer}
                onRemoveTopic={filterState.toggleTopic}
                onRemoveProgress={filterState.toggleProgress}
                onRemoveStatus={filterState.toggleStatus}
            >
                {filterGroups}
            </MobileFilters>

            {filteredStreams.length === 0 ? (
                <div className="flex flex-col items-center gap-5 py-10">
                    <Text as="h2" className="text-center">
                        {t('noContentFound')}
                    </Text>
                    <Button variant="bordered-white" color="slate-grey" onPress={filterState.clearAll}>
                        {t('reset')}
                    </Button>
                </div>
            ) : (
                <CourseGrid
                    streams={filteredStreams}
                    index={index}
                    displayMode={displayMode}
                    topicFilters={filters.topics}
                    otherTopicName={t('otherTopic')}
                    accessByStreamId={accessByStreamId}
                    onPressStream={onPressStream}
                    toolbar={<DisplayModeToggle displayMode={displayMode} onChange={setDisplayMode} />}
                    resetKey={JSON.stringify(filters)}
                />
            )}

            {unavailableStream && unavailableAccess && (
                <CourseUnavailableModal
                    stream={unavailableStream}
                    access={unavailableAccess}
                    onNotify={notify}
                    onClose={() => setUnavailableStream(null)}
                />
            )}
        </div>
    );
};
